import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, CalendarClock, School, Megaphone, PoundSterling, CalendarDays, UserPlus, Star } from "lucide-react";
import { api } from "@/lib/api";
import { gbp } from "@/data/marketingOptions";

const Wave = ({ color }) => (
  <svg className="pointer-events-none absolute inset-x-0 bottom-0 h-10 w-full" viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 22 C90 44 150 4 210 18 C270 32 330 6 400 20 L400 40 L0 40 Z" fill={color} opacity="0.1" />
  </svg>
);

const PrimaryCard = ({ label, value, hint, icon: Icon, color, state, index, to }) => (
  <Link to={to} data-testid={`marketing-primary-${index}`} aria-label={`${label} — open destination`} className="group relative block overflow-hidden rounded-2xl border border-[#E4E9F3] bg-white p-5 pt-6 shadow-[0_3px_14px_rgba(20,36,95,0.06)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(20,36,95,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5A4]/40">
    <span className="absolute inset-x-0 top-0 h-2" style={{ backgroundColor: color }} />
    <div className="relative flex items-start justify-between">
      <div>
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#5A6B8C]">{label}</span>
        {state && <span className="ml-2 inline-flex h-2 w-2 rounded-full align-middle" style={{ backgroundColor: state }} />}
      </div>
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-sm" style={{ backgroundColor: color }}>
        <Icon size={20} strokeWidth={2.3} />
      </span>
    </div>
    <p className="relative mt-3 font-numeric text-4xl font-bold leading-none tracking-tight text-[#14245F]">{value}</p>
    {hint && <p className="relative mt-1.5 text-xs font-semibold text-[#5A6B8C]">{hint}</p>}
    <Wave color={color} />
  </Link>
);

const CompactCard = ({ label, value, hint, icon: Icon, color, index, to }) => (
  <Link to={to} data-testid={`marketing-secondary-${index}`} aria-label={`${label} — open destination`} className="flex items-center gap-3 rounded-2xl border border-[#E4E9F3] bg-white p-3.5 shadow-[0_2px_10px_rgba(20,36,95,0.05)] transition-all duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5A4]/40">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}18`, color }}>
      <Icon size={18} strokeWidth={2.3} />
    </span>
    <div className="min-w-0 leading-tight">
      <p className="font-numeric text-xl font-bold text-[#14245F]">{value}</p>
      <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-[#5A6B8C]">{label}</p>
      {hint && <p className="truncate text-[11px] text-[#5A6B8C]">{hint}</p>}
    </div>
  </Link>
);

export const MarketingSummary = ({ refreshKey }) => {
  const [s, setS] = useState(null);
  const load = useCallback(async () => { try { setS(await api.list("marketing-summary")); } catch { setS(null); } }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const primary = [
    { label: "Pre-launch Sign-ups", value: s?.prelaunch_signups ?? "—", icon: UserPlus, color: "#22D3EE", state: "#16A34A", to: "/marketing?tab=overview" },
    { label: "Paid Subscriptions", value: s?.paid_subscriptions ?? "—", icon: Star, color: "#16A34A", state: "#16A34A", to: "/marketing?tab=overview" },
    { label: "Campaign Spend", value: s ? gbp(s.actual_spend) : "—", hint: s ? `of ${gbp(s.total_campaign_budget)} budget` : undefined, icon: PoundSterling, color: "#E0A82E", to: "/marketing?tab=campaigns" },
    { label: "Active Campaigns", value: s?.active_campaigns ?? "—", icon: Megaphone, color: "#0EA5A4", state: "#16A34A", to: "/marketing?tab=campaigns&status=Active" },
  ];
  const secondary = [
    { label: "Active Influencers", value: s?.active_influencers ?? "—", icon: Users, color: "#EC4899", to: "/marketing?tab=influencers&filter=active" },
    { label: "Follow-ups Due", value: s?.influencer_followups_due ?? "—", icon: CalendarClock, color: "#FB6F5A", to: "/marketing?tab=influencers&filter=followup-due" },
    { label: "Schools Contacted", value: s?.schools_contacted ?? "—", hint: s ? `${s.schools_pilot_interested} pilot-interested` : undefined, icon: School, color: "#1D4ED8", to: "/marketing?tab=schools&filter=contacted" },
    { label: "Upcoming Content", value: s?.upcoming_content ?? "—", icon: CalendarDays, color: "#7C3AED", to: "/marketing?tab=content&filter=upcoming" },
  ];

  return (
    <div data-testid="marketing-summary" className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {primary.map((c, i) => <PrimaryCard key={c.label} index={i} {...c} />)}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {secondary.map((c, i) => <CompactCard key={c.label} index={i} {...c} />)}
      </div>
    </div>
  );
};
